import { useState } from "react";
import { usePresenceContexts, usePresenceContextDetail, useCreatePresenceFocus } from "@/lib/presenceQueries";

const PROGRESS_OPTIONS = ["not_started", "exploring", "practising", "integrating", "applied"];
const PROGRESS_LABEL = {
  not_started: "Not started", exploring: "Exploring", practising: "Practising", integrating: "Integrating", applied: "Applied",
};

/**
 * Master v5 Prompt 21 — coach's read/oversight surface for a specific
 * member's Presence Lab: their real leadership contexts, the full
 * focus history (never overwritten — a new focus resolves the prior
 * one), and their own logged real-world applications (coach reads
 * these as evidence, never writes them).
 */
export default function PresenceTab({ workspaceId, memberId, overview }) {
  const [openId, setOpenId] = useState(null);
  const { data, isPending, isError } = usePresenceContexts(workspaceId, memberId);

  if (overview.programme_type !== "executive_presence") {
    return <p className="text-sm text-ink-soft" data-testid="client-360-presence-not-applicable">This Workspace's programme isn't Executive Presence.</p>;
  }
  if (isPending) return <p className="text-sm text-ink-soft" data-testid="client-360-presence-loading">Loading…</p>;
  if (isError) return <p className="text-sm text-red-600" data-testid="client-360-presence-error">Couldn't load Presence Lab data.</p>;

  const items = data?.items || [];
  if (items.length === 0) return <p className="text-sm text-ink-soft" data-testid="client-360-presence-empty">This member hasn't added a leadership context yet.</p>;

  return (
    <div className="space-y-2" data-testid="client-360-presence-tab">
      {items.map((c) => (
        <div key={c.psc_id} className="border border-ink/10 rounded-lg bg-white/50" data-testid={`client-360-presence-context-${c.psc_id}`}>
          <button
            onClick={() => setOpenId(openId === c.psc_id ? null : c.psc_id)}
            className="w-full text-left p-3 flex items-center justify-between gap-3"
            data-testid={`client-360-presence-context-toggle-${c.psc_id}`}
          >
            <div>
              <p className="text-sm text-ink font-medium">{c.title}</p>
              {c.situation_text && <p className="text-xs text-ink-soft line-clamp-1">{c.situation_text}</p>}
            </div>
            <span className="text-xs text-terracotta shrink-0">{openId === c.psc_id ? "Hide" : "Manage focus"}</span>
          </button>
          {openId === c.psc_id && <ContextFocusPanel workspaceId={workspaceId} memberId={memberId} pscId={c.psc_id} />}
        </div>
      ))}
    </div>
  );
}

function ContextFocusPanel({ workspaceId, memberId, pscId }) {
  const { data, isPending } = usePresenceContextDetail(workspaceId, memberId, pscId);
  const [form, setForm] = useState({ focus_text: "", evidence_text: "", coach_rationale_text: "", application_prompt_text: "", progress_state: "not_started" });
  const createFocus = useCreatePresenceFocus(workspaceId, memberId, pscId);

  async function handleSubmit() {
    if (!form.focus_text.trim()) return;
    await createFocus.mutateAsync(form);
    setForm({ focus_text: "", evidence_text: "", coach_rationale_text: "", application_prompt_text: "", progress_state: "not_started" });
  }

  if (isPending || !data) return <p className="text-xs text-ink-soft p-3" data-testid={`client-360-presence-focus-loading-${pscId}`}>Loading…</p>;

  return (
    <div className="border-t border-ink/10 p-3 space-y-3" data-testid={`client-360-presence-focus-panel-${pscId}`}>
      {data.foci.length > 0 && (
        <div className="space-y-1.5">
          {data.foci.map((f) => (
            <div key={f.psf_id} className={`text-xs p-2 rounded border ${f.status === "active" ? "border-terracotta/30 bg-terracotta/5" : "border-ink/10 opacity-60"}`} data-testid={`client-360-presence-focus-history-${f.psf_id}`}>
              <span className="font-medium text-ink">{f.focus_text}</span> — <span className="uppercase text-ink-soft">{PROGRESS_LABEL[f.progress_state]}</span>
              {f.status === "resolved" && <span className="text-ink-soft"> (resolved)</span>}
            </div>
          ))}
        </div>
      )}
      {data.applications.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs uppercase text-ink-soft">Applications logged by the member</p>
          {data.applications.map((a) => (
            <p key={a.psa_id} className="text-xs text-ink-soft" data-testid={`client-360-presence-application-${a.psa_id}`}>{a.description_text}</p>
          ))}
        </div>
      )}
      <div className="space-y-1.5 pt-2 border-t border-ink/10">
        <p className="text-xs uppercase text-ink-soft">Set a new focus</p>
        <input placeholder="Qualitative focus" value={form.focus_text} onChange={(e) => setForm({ ...form, focus_text: e.target.value })} className="w-full border border-ink/15 rounded px-2 py-1.5 text-xs" data-testid={`client-360-presence-focus-text-input-${pscId}`} />
        <input placeholder="Evidence" value={form.evidence_text} onChange={(e) => setForm({ ...form, evidence_text: e.target.value })} className="w-full border border-ink/15 rounded px-2 py-1.5 text-xs" data-testid={`client-360-presence-evidence-input-${pscId}`} />
        <input placeholder="Coach rationale" value={form.coach_rationale_text} onChange={(e) => setForm({ ...form, coach_rationale_text: e.target.value })} className="w-full border border-ink/15 rounded px-2 py-1.5 text-xs" data-testid={`client-360-presence-rationale-input-${pscId}`} />
        <input placeholder="Suggested real-world application" value={form.application_prompt_text} onChange={(e) => setForm({ ...form, application_prompt_text: e.target.value })} className="w-full border border-ink/15 rounded px-2 py-1.5 text-xs" data-testid={`client-360-presence-application-prompt-input-${pscId}`} />
        <select value={form.progress_state} onChange={(e) => setForm({ ...form, progress_state: e.target.value })} className="w-full border border-ink/15 rounded px-2 py-1.5 text-xs" data-testid={`client-360-presence-progress-select-${pscId}`}>
          {PROGRESS_OPTIONS.map((p) => <option key={p} value={p}>{PROGRESS_LABEL[p]}</option>)}
        </select>
        {createFocus.isError && <p className="text-xs text-red-600" data-testid={`client-360-presence-focus-error-${pscId}`}>Couldn't save this focus.</p>}
        <button onClick={handleSubmit} disabled={!form.focus_text.trim() || createFocus.isPending} className="text-xs font-medium bg-terracotta text-paper px-3 py-1.5 rounded-full hover:bg-terracotta/90 disabled:opacity-50" data-testid={`client-360-presence-focus-submit-${pscId}`}>
          {createFocus.isPending ? "Saving…" : "Set focus"}
        </button>
      </div>
    </div>
  );
}
